import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class UheatProductBulkOrder extends HTMLElement {
  #abortController = new AbortController();

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    const { signal } = this.#abortController;
    this.#section = this.closest('.shopify-section');
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('change', this.#handleInput, { signal });
    this.#section?.addEventListener('change', this.#handleAddOnChange, { signal });
    this.#section?.addEventListener('uheat:add-on-quantity-update', this.#handleAddOnQuantityChange, { signal });
    this.#updateSummary();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #inputs() {
    return /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('[data-bulk-quantity]'));
  }

  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bulk-submit]'));
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('button') : null;
    if (!target) return;

    const action = target.dataset.bulkAction;
    if (action) {
      event.preventDefault();
      const row = target.closest('[data-bulk-row]');
      const input = /** @type {HTMLInputElement | null} */ (row?.querySelector('[data-bulk-quantity]'));
      if (!input || input.disabled) return;
      this.#adjustQuantity(input, action === 'increase' ? 1 : -1);
      return;
    }

    if (target.hasAttribute('data-bulk-submit')) {
      event.preventDefault();
      this.#addSelectedItems();
    }
  };

  #handleInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.hasAttribute('data-bulk-quantity')) return;
    this.#normaliseQuantity(event.target);
    this.#updateSummary();
  };

  #handleAddOnChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('uheat-product-addons input[data-add-on-choice]')) return;
    this.#updateSummary();
  };

  #handleAddOnQuantityChange = () => {
    this.#updateSummary();
  };

  #adjustQuantity(input, direction) {
    const current = Number(input.value) || 0;
    const minimum = Number(input.dataset.minimum) || 1;
    const increment = Number(input.step) || 1;
    const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    let next;

    if (direction > 0) {
      next = current === 0 ? minimum : current + increment;
      next = Math.min(next, maximum);
    } else {
      next = current <= minimum ? 0 : Math.max(0, current - increment);
    }

    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #normaliseQuantity(input) {
    const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    const minimum = Number(input.dataset.minimum) || 1;
    const increment = Number(input.step) || 1;
    let value = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);

    if (value > 0) {
      value = Math.max(minimum, value);
      value = minimum + Math.round((value - minimum) / increment) * increment;
    }

    input.value = String(Math.min(value, maximum));
  }

  #getSelectedItems() {
    return Array.from(this.#inputs)
      .map((input) => ({
        id: Number(input.dataset.variantId),
        quantity: Number(input.value) || 0,
        price: Number(input.dataset.price) || 0,
      }))
      .filter((item) => item.id && item.quantity > 0);
  }

  #getSelectedAddOns() {
    const addOnSelector = this.#section?.querySelector('uheat-product-addons');

    if (typeof addOnSelector?.getSelectedItems === 'function') {
      return addOnSelector.getSelectedItems().map((item) => ({
        id: Number(item.variantId),
        price: Number(item.price) || 0,
        quantity: Math.max(1, Number(item.quantity) || 1),
      })).filter((item) => item.id);
    }

    const input = /** @type {HTMLInputElement | null} */ (
      this.#section?.querySelector('uheat-product-addons input[data-add-on-variant]:checked') || null
    );

    if (!input?.value) return [];

    const quantity =
      typeof addOnSelector?.getSelectedQuantity === 'function'
        ? addOnSelector.getSelectedQuantity()
        : 1;

    return [{
      id: Number(input.value),
      price: Number(input.dataset.addOnPriceCents) || 0,
      quantity: Math.max(1, Number(quantity) || 1),
    }];
  }

  #getCartItems(selectedItems) {
    const items = selectedItems.map((item) => ({ ...item }));
    const selectedQuantity = selectedItems.reduce((total, item) => total + item.quantity, 0);
    const addOns = this.#getSelectedAddOns();

    if (selectedQuantity > 0) {
      for (const addOn of addOns) {
        const existingItem = items.find((item) => item.id === addOn.id);
        if (existingItem) {
          existingItem.quantity += addOn.quantity;
        } else {
          items.push({ id: addOn.id, quantity: addOn.quantity, price: addOn.price });
        }
      }
    }

    return items;
  }

  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || 'en-GB', {
        style: 'currency',
        currency: this.dataset.currency || 'GBP',
      }).format(cents / 100);
    } catch (_error) {
      return `£${(cents / 100).toFixed(2)}`;
    }
  }

  #updateSummary() {
    const items = this.#getSelectedItems();
    const itemCount = items.reduce((total, item) => total + item.quantity, 0);
    const mainPriceTotal = items.reduce((total, item) => total + item.quantity * item.price, 0);
    const addOns = this.#getSelectedAddOns();
    const addOnTotal = addOns.reduce((total, addOn) => total + addOn.price * addOn.quantity, 0);
    const priceTotal = mainPriceTotal + addOnTotal;
    const countElement = this.querySelector('[data-bulk-count]');
    const totalElement = this.querySelector('[data-bulk-total]');
    const button = this.#button;

    this.dataset.selectedQuantity = String(itemCount);
    this.dataset.selectedTotal = String(mainPriceTotal);

    if (countElement) {
      const singular = this.dataset.itemSingular || 'item selected';
      const plural = this.dataset.itemPlural || 'items selected';
      countElement.textContent = `${itemCount} ${itemCount === 1 ? singular : plural}`;
    }
    if (totalElement) totalElement.textContent = this.#formatMoney(priceTotal);
    if (button) button.disabled = itemCount === 0;

    this.dispatchEvent(new CustomEvent('uheat:bulk-order-update', {
      bubbles: true,
      detail: { quantity: itemCount, mainTotal: mainPriceTotal, total: priceTotal },
    }));
  }

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent?.fetchCartData) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  async #addSelectedItems() {
    const selectedItems = this.#getSelectedItems();
    const items = this.#getCartItems(selectedItems);
    const button = this.#button;
    const status = this.querySelector('[data-bulk-status]');
    if (!selectedItems.length || !button) return;

    button.disabled = true;
    button.classList.add('is-loading');
    if (status) {
      status.hidden = true;
      status.classList.remove('is-error', 'is-success');
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = Array.from(cartItemsComponents)
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
      .filter(Boolean);
    const totalQuantity = items.reduce((total, item) => total + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({ id: item.id, quantity: item.quantity })),
          sections: sectionIds.join(','),
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add the selected items.');

      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'uheat-product-bulk-order',
          sourceId: this.id,
          itemCount: totalQuantity,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });

      this.#inputs.forEach((input) => { input.value = '0'; });
      this.#updateSummary();
      if (status) {
        status.textContent = this.dataset.successMessage || 'Your selected items were added to the basket.';
        status.classList.add('is-success');
        status.hidden = false;
      }
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Unable to add the selected items.',
        code: 'INVALID',
      }));
      if (status) {
        status.textContent = error instanceof Error ? error.message : 'Unable to add the selected items.';
        status.classList.add('is-error');
        status.hidden = false;
      }
    } finally {
      button.classList.remove('is-loading');
      this.#updateSummary();
    }
  }
}

if (!customElements.get('uheat-product-bulk-order')) {
  customElements.define('uheat-product-bulk-order', UheatProductBulkOrder);
}
